/**
 * Health service covering backend availability endpoints, refactored from api.ts with unchanged behavior.
 */

import api from "@/utils/axios";
import { API_BASE_URL } from "./apiBase";

/**
 * Performs a lightweight backend availability check.
 *
 * Calls `/api/health/ping` and returns `true` when the backend responds
 * with a successful HTTP status.
 *
 * @returns {Promise<boolean>} whether the backend is reachable
 */
export async function healthPing(): Promise<boolean> {
  const response = await api.get(`${API_BASE_URL}/api/health/ping`, {
    headers: { Accept: "application/json", "Cache-Control": "no-store" },
    validateStatus: () => true,
  });
  return response.status >= 200 && response.status < 300;
}

/**
 * Checks whether the backend can successfully reach the database.
 *
 * Calls `/api/health/database` and evaluates the JSON `status` field.
 * Returns `true` when the backend reports `"ok"`, otherwise `false`.
 *
 * @returns {Promise<boolean>} whether the database is available
 */
export async function databaseHealth(): Promise<boolean> {
  const response = await api.get(`${API_BASE_URL}/api/health/database`, {
    headers: { Accept: "application/json", "Cache-Control": "no-store" },
    validateStatus: () => true,
    transformResponse: (data) => data,
  });
  if (response.status < 200 || response.status >= 300) return false;
  try {
    const body =
      typeof response.data === "string"
        ? JSON.parse(response.data)
        : response.data;
    return body?.status === "ok";
  } catch {
    return false;
  }
}
